import {
  Badge, Box, Text, Flex, Spinner, Skeleton, Button, IconButton,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody,
  useDisclosure,
} from "@chakra-ui/react";
import { cloneElement, useEffect, useMemo, useState } from "react";
import { CategoryCard } from "../../../components/Admin/CategoryCard";
import { ProductCard } from "../../../components/Admin/ProductCard";
import { SubCategoryCard } from "../../../components/Admin/SubcategoryCard";
import { TypeCard } from "../../../components/Admin/TypeCard";
import { Sales } from "../../../components/Admin/Sales";
import { OrdersCard } from "../../../components/Admin/OrdersCard";
import { OrdersChart } from "../../../components/Admin/OrdersChart";
import { DeliveryChart } from "../../../components/Admin/DeliveryChart";
import { CouponCard } from "../../../components/Admin/CouponCard";
import { UserCard } from "../../../components/Admin/UserCard";
import { CreditsOverviewCard } from "../../../components/Admin/CreditsOverviewCard";
import { OverdueOrdersAlert } from "../../../components/Admin/OverdueOrdersAlert";
import { NegativeStockAlert } from "../../../components/Admin/NegativeStockAlert";
import { CalculatorCurationCard } from "../../../components/Admin/CalculatorCurationCard";
import { DeliveryStatusChart } from "../../../components/Admin/DeliveryStatusChart";
import { OrdersByType } from "../../../components/Admin/OrdersByType";
import { PaymentMethodChart } from "../../../components/Admin/PaymentMethodChart";
import { UsersChart } from "../../../components/Admin/UsersChart";
import { api } from "../../../services/api";
import { useAuth } from "../../../Hooks/useAuth";
import {
  FiHome, FiPackage, FiGrid, FiTag, FiLayers,
  FiPercent, FiBarChart2, FiLogOut, FiMenu, FiX,
  FiDollarSign, FiUsers, FiBox, FiMaximize2,
  FiAlertTriangle, FiAlertCircle, FiGift, FiSliders, FiClipboard,
} from "react-icons/fi";

const GREEN  = "#004a33";
const ORANGE = "#fc8f04";
const SIDEBAR_W = "220px";

const NAV = [
  { key: "",             label: "Dashboard",    Icon: FiHome },
  { key: "Relatório",   label: "Relatório",    Icon: FiBarChart2 },
  { key: "Pedidos",     label: "Pedidos",      Icon: FiClipboard },
  { key: "Produtos",    label: "Produtos",     Icon: FiPackage },
  { key: "Categorias",  label: "Categorias",   Icon: FiGrid },
  { key: "Subcategorias", label: "Subcategorias", Icon: FiLayers },
  { key: "Tipos",       label: "Tipos",        Icon: FiTag },
  { key: "Cupons",      label: "Cupons",       Icon: FiPercent },
  { key: "Usuários",    label: "Usuários",     Icon: FiUsers },
  { key: "Créditos",   label: "Créditos",     Icon: FiGift },
  { key: "Calculadora", label: "Calculadora",  Icon: FiSliders },
];

function StatCard({ label, value, sub, Icon, accent, loading }) {
  return (
    <Box
      bg="white" borderRadius="16px" p="20px 24px" flex="1" minW="180px"
      border="1px solid rgba(0,74,51,.08)" boxShadow="0 2px 14px rgba(0,74,51,.06)"
      position="relative" overflow="hidden"
    >
      <Box position="absolute" top="0" left="0" w="4px" h="100%" bg={accent || GREEN} borderRadius="2px 0 0 2px" />
      <Flex justify="space-between" align="flex-start" pl="10px">
        <Box>
          <Text fontSize="11px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="0.08em" mb="8px">{label}</Text>
          {loading ? (
            <Skeleton height="32px" width="120px" borderRadius="6px" />
          ) : (
            <Text fontSize="28px" fontWeight="900" color={GREEN} lineHeight="1.1">{value}</Text>
          )}
          {sub && !loading && <Text fontSize="12px" color="gray.400" mt="6px">{sub}</Text>}
        </Box>
        <Box bg={`${accent || GREEN}14`} p="10px" borderRadius="12px" flexShrink={0} mt="2px">
          <Icon size={20} color={accent || GREEN} />
        </Box>
      </Flex>
    </Box>
  );
}

function ChartCard({ title, description, children }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  return (
    <>
      <Box
        bg="white" borderRadius="16px" border="1px solid rgba(0,74,51,.08)"
        boxShadow="0 2px 12px rgba(0,74,51,.05)" overflow="hidden"
      >
        {title && (
          <Flex
            px="20px" pt="16px" pb="4px" borderBottom="1px solid rgba(0,74,51,.05)"
            justify="space-between" align="center" gap="8px"
          >
            <Text fontSize="12px" fontWeight="800" color="gray.400" textTransform="uppercase" letterSpacing="0.08em">
              {title}
            </Text>
            <IconButton
              aria-label="Expandir gráfico"
              icon={<FiMaximize2 size={13} />}
              size="xs"
              variant="ghost"
              color="gray.400"
              onClick={onOpen}
              _hover={{ color: GREEN, bg: "rgba(0,74,51,.08)" }}
            />
          </Flex>
        )}
        <Box p="16px">{children}</Box>
      </Box>

      <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered>
        <ModalOverlay />
        <ModalContent borderRadius="16px" mx="16px">
          <ModalHeader fontSize="16px" fontWeight="900" color={GREEN} pr="48px">{title}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb="28px">
            {description && (
              <Text fontSize="13px" color="gray.500" mb="20px" lineHeight="1.5">{description}</Text>
            )}
            <Box>{cloneElement(children, { height: "440px" })}</Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}

function AlertCard({ title, Icon, accent, children }) {
  return (
    <Box
      bg="white" borderRadius="16px" border={`1px solid ${accent}40`}
      boxShadow="0 2px 12px rgba(0,74,51,.05)" p="16px 18px"
    >
      <Flex align="center" gap="8px" mb="10px">
        <Icon size={15} color={accent} />
        <Text fontSize="12px" fontWeight="800" color={accent}>{title}</Text>
      </Flex>
      {children}
    </Box>
  );
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const EXPIRING_SOON_DAYS = 7;

// Client-side only — both `products` and `batches` already arrive in the
// same get_infos_for_dashboard payload, so no extra endpoint is needed to
// surface "lotes vencendo" / "produtos sem lote ativo" (previously invisible
// until an admin happened to notice while browsing the catalog).
function StockAlerts({ products, batches }) {
  const { expiringSoon, withoutActiveBatch } = useMemo(() => {
    const now = new Date();
    const soonCutoff = new Date(now.getTime() + EXPIRING_SOON_DAYS * MS_PER_DAY);
    const productById = Object.fromEntries((products || []).map(p => [p.id, p]));

    const expiring = (batches || [])
      .filter(b => {
        const exp = new Date(b.expiration_date);
        return exp >= now && exp <= soonCutoff;
      })
      .map(b => ({ ...b, product: productById[b.product_id] }))
      .sort((a, b) => new Date(a.expiration_date) - new Date(b.expiration_date));

    const activeProductIds = new Set(
      (batches || [])
        .filter(b => new Date(b.start_date) <= now && new Date(b.expiration_date) >= now)
        .map(b => b.product_id)
    );
    const withoutBatch = (products || []).filter(p => !activeProductIds.has(p.id));

    return { expiringSoon: expiring, withoutActiveBatch: withoutBatch };
  }, [products, batches]);

  if (expiringSoon.length === 0 && withoutActiveBatch.length === 0) return null;

  return (
    <Box display="grid" gridTemplateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap="16px" mb="24px">
      {expiringSoon.length > 0 && (
        <AlertCard title={`Lotes expirando em ${EXPIRING_SOON_DAYS} dias (${expiringSoon.length})`} Icon={FiAlertTriangle} accent={ORANGE}>
          <Box display="flex" flexDirection="column" gap="2px">
            {expiringSoon.slice(0, 6).map(b => {
              const daysLeft = Math.ceil((new Date(b.expiration_date) - new Date()) / MS_PER_DAY);
              return (
                <Flex key={b.id} justify="space-between" align="center" py="5px" borderBottom="1px solid rgba(0,74,51,.05)">
                  <Text fontSize="12px" color="gray.700" noOfLines={1}>{b.product?.name || `Lote #${b.id}`}</Text>
                  <Badge colorScheme="orange" fontSize="9px" borderRadius="4px" flexShrink={0}>
                    {daysLeft <= 0 ? "Expira hoje" : `${daysLeft}d`}
                  </Badge>
                </Flex>
              );
            })}
          </Box>
          {expiringSoon.length > 6 && (
            <Text fontSize="11px" color="gray.400" mt="6px">+{expiringSoon.length - 6} outro{expiringSoon.length - 6 !== 1 ? "s" : ""}</Text>
          )}
        </AlertCard>
      )}
      {withoutActiveBatch.length > 0 && (
        <AlertCard title={`Produtos sem lote ativo (${withoutActiveBatch.length})`} Icon={FiAlertCircle} accent="#e74c3c">
          <Box display="flex" flexDirection="column" gap="2px">
            {withoutActiveBatch.slice(0, 6).map(p => (
              <Text key={p.id} fontSize="12px" color="gray.700" noOfLines={1} py="5px" borderBottom="1px solid rgba(0,74,51,.05)">
                {p.name}
              </Text>
            ))}
          </Box>
          {withoutActiveBatch.length > 6 && (
            <Text fontSize="11px" color="gray.400" mt="6px">+{withoutActiveBatch.length - 6} outro{withoutActiveBatch.length - 6 !== 1 ? "s" : ""}</Text>
          )}
        </AlertCard>
      )}
    </Box>
  );
}

export function Dashboard() {
  const { user } = useAuth();
  const [activeTab, setActiveTab]         = useState("");
  const [batchOrders, setBatchOrders]     = useState([]);
  const [users, setUsers]                 = useState([]);
  const [batches, setBatches]             = useState([]);
  const [products, setProducts]           = useState([]);
  const [typesSold, setTypesSold]         = useState([]);
  const [payments, setPayments]           = useState([]);
  const [productTypes, setProductTypes]   = useState([]);
  const [categories, setCategories]       = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [prices, setPrices]               = useState([]);
  const [loading, setLoading]             = useState(true);
  const [sidebarOpen, setSidebarOpen]     = useState(false);

  async function fetchDashboard() {
    try {
      const response = await api.get("/api/v1/admin/panels/get_infos_for_dashboard");
      setProductTypes(response.data.product_types);
      setUsers(response.data.users);
      setBatchOrders(response.data.batch_orders);
      setBatches(response.data.batches);
      setPrices(response.data.prices);
      setProducts(response.data.products);
      setPayments(response.data.payments);
      setCategories(response.data.categories);
      setSubcategories(response.data.subcategories);
      setTypesSold(response.data.product_types_sold);
    } catch (error) {
      console.error("Error fetching dashboard:", error);
    } finally {
      setLoading(false);
    }
  }

  // Admin access is already enforced by <RequireAdmin> before this component
  // mounts (see routes.jsx) — this effect only fires once that's confirmed.
  useEffect(() => { fetchDashboard(); }, []);

  const paidOrders  = batchOrders.filter(o => o.paid);
  const totalRevenue = paidOrders.reduce((n, { amount }) => n + amount, 0);

  function renderContent() {
    switch (activeTab) {
      case "Subcategorias":
        return (
          <SubCategoryCard
            allSubcategories={subcategories}
            allCategories={categories}
            allProductTypes={productTypes}
            allProducts={products}
          />
        );
      case "Categorias":
        return (
          <CategoryCard
            allCategories={categories}
            allSubcategories={subcategories}
            allProductTypes={productTypes}
            allProducts={products}
          />
        );
      case "Tipos":
        return <TypeCard allCategories={categories} allProductTypes={productTypes} />;
      case "Produtos":
        return (
          <ProductCard
            allProducts={products}
            allProductTypes={productTypes}
            allCategories={categories}
            allSubcategories={subcategories}
            allBatches={batches}
            allPrices={prices.reduce((acc, curr) => acc.concat(curr), [])}
          />
        );
      case "Relatório":
        return <Sales />;
      case "Pedidos":
        return <OrdersCard />;
      case "Cupons":
        return <CouponCard />;
      case "Usuários":
        return <UserCard />;
      case "Créditos":
        return <CreditsOverviewCard />;
      case "Calculadora":
        return <CalculatorCurationCard allProductTypes={productTypes} allProducts={products} />;
      default:
        return (
          <Box>
            {/* Header */}
            <Box mb="24px">
              <Text fontSize="11px" fontWeight="800" color="gray.400" textTransform="uppercase" letterSpacing="0.1em" mb="4px">
                Painel administrativo
              </Text>
              <Text fontSize="26px" fontWeight="900" color={GREEN} lineHeight="1.2">
                Olá, {user?.full_name?.split(" ")[0] || user?.email} 👋
              </Text>
              <Text fontSize="13px" color="gray.400" mt="4px">
                {new Date().toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}
              </Text>
            </Box>

            {/* Stat cards */}
            <Flex gap="16px" flexWrap="wrap" mb="28px">
              <StatCard
                label="Receita (pedidos pagos)"
                value={totalRevenue.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                sub={`${paidOrders.length} pedidos confirmados`}
                Icon={FiDollarSign}
                accent={GREEN}
                loading={loading}
              />
              <StatCard
                label="Usuários cadastrados"
                value={users.length || "—"}
                sub="Total na plataforma"
                Icon={FiUsers}
                accent={ORANGE}
                loading={loading}
              />
              <StatCard
                label="Produtos ativos"
                value={products.length || "—"}
                sub={`${batches.length} lote${batches.length !== 1 ? "s" : ""} aberto${batches.length !== 1 ? "s" : ""}`}
                Icon={FiBox}
                accent="#2980b9"
                loading={loading}
              />
            </Flex>

            {/* Alertas */}
            {!loading && <OverdueOrdersAlert />}
            {!loading && <NegativeStockAlert />}
            {!loading && <StockAlerts products={products} batches={batches} />}

            {/* Charts 2-col grid */}
            {loading ? (
              <Box display="grid" gridTemplateColumns={{ base: "1fr", xl: "1fr 1fr" }} gap="20px">
                {[...Array(6)].map((_, i) => (
                  <Skeleton key={i} height="320px" borderRadius="16px" />
                ))}
              </Box>
            ) : (
              <Box display="grid" gridTemplateColumns={{ base: "1fr", xl: "1fr 1fr" }} gap="20px">
                <ChartCard
                  title="Pedidos mensais (pagos vs abertos)"
                  description="Soma o valor dos pedidos feitos em cada mês do ano atual, separando os que já foram pagos dos que ainda estão em aberto. Ajuda a comparar o volume de vendas confirmadas com o que ainda está pendente de pagamento mês a mês."
                >
                  <OrdersChart orders={batchOrders} />
                </ChartCard>
                <ChartCard
                  title="Faturamento por método de pagamento"
                  description="Soma o valor dos pagamentos com status pago em cada mês do ano atual, separando PIX de cartão. Mostra qual forma de pagamento gera mais faturamento e como isso varia ao longo do ano."
                >
                  <PaymentMethodChart payments={payments} />
                </ChartCard>
                <ChartCard
                  title="Status de entregas por mês"
                  description="Conta os pedidos pagos em cada mês do ano atual, agrupados pelo status de entrega (pendente, em entrega ou entregue). Ajuda a identificar meses com acúmulo de entregas não finalizadas."
                >
                  <DeliveryStatusChart orders={batchOrders} />
                </ChartCard>
                <ChartCard
                  title="Novos usuários por mês"
                  description="Conta quantos usuários se cadastraram em cada mês do ano atual, com base na data de criação da conta. Mostra a evolução do crescimento da base de usuários."
                >
                  <UsersChart users={users} />
                </ChartCard>
                <ChartCard
                  title="Entrega vs Retirada (pedidos pagos)"
                  description="Mostra a proporção entre pedidos pagos que optaram por entrega e os que optaram por retirada no local, considerando todo o histórico de pedidos pagos."
                >
                  <DeliveryChart orders={batchOrders} />
                </ChartCard>
                <ChartCard
                  title="Faturamento por tipo de produto"
                  description="Mostra o faturamento pago mês a mês do ano atual, com uma linha para cada tipo de produto. Ajuda a identificar quais tipos de produto mais vendem e em quais meses."
                >
                  <OrdersByType typesSold={typesSold} />
                </ChartCard>
              </Box>
            )}
          </Box>
        );
    }
  }

  return (
    <Box minH="100vh" bg="#f4f6f8" display="flex">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <Box
          position="fixed" inset="0" bg="rgba(0,0,0,.45)" zIndex="99"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <Box
        position="fixed" top="0" left="0" bottom="0"
        w={SIDEBAR_W} bg={GREEN} zIndex="100"
        display="flex" flexDir="column"
        transform={{ base: sidebarOpen ? "translateX(0)" : `translateX(-${SIDEBAR_W})`, md: "translateX(0)" }}
        transition="transform 0.22s ease"
        boxShadow="4px 0 24px rgba(0,0,0,.14)"
      >
        {/* Brand */}
        <Box px="22px" py="22px" borderBottom="1px solid rgba(255,255,255,.1)">
          <Text fontSize="22px" fontWeight="900" color={ORANGE} letterSpacing="-0.5px">BOOZEN</Text>
          <Text fontSize="11px" color="rgba(255,255,255,.4)" fontWeight="600" mt="1px" textTransform="uppercase" letterSpacing="0.08em">
            Admin
          </Text>
        </Box>

        {/* Nav */}
        <Box flex="1" py="10px" overflowY="auto">
          {NAV.map(({ key, label, Icon }) => {
            const active = activeTab === key;
            return (
              <Button
                key={key}
                variant="ghost"
                w="100%"
                justifyContent="flex-start"
                leftIcon={<Icon size={15} />}
                px="22px"
                py="10px"
                h="auto"
                borderRadius="0"
                borderLeft={`3px solid ${active ? ORANGE : "transparent"}`}
                bg={active ? `rgba(252,143,4,.14)` : "transparent"}
                color={active ? ORANGE : "rgba(255,255,255,.65)"}
                fontWeight={active ? "800" : "500"}
                fontSize="13.5px"
                _hover={{ bg: "rgba(252,143,4,.1)", color: ORANGE, borderLeftColor: ORANGE }}
                _active={{ bg: "rgba(252,143,4,.18)" }}
                onClick={() => { setActiveTab(key); setSidebarOpen(false); }}
                transition="all 0.15s"
              >
                {label}
              </Button>
            );
          })}
        </Box>

        {/* Footer */}
        <Box px="22px" py="16px" borderTop="1px solid rgba(255,255,255,.1)">
          <Button
            variant="ghost" size="sm" w="100%" justifyContent="flex-start"
            leftIcon={<FiLogOut size={13} />}
            color="rgba(255,255,255,.4)" fontSize="12px" fontWeight="600"
            _hover={{ color: "white", bg: "rgba(255,255,255,.06)" }}
            onClick={() => { window.location.href = "/"; }}
            borderRadius="8px"
          >
            Ir para o site
          </Button>
        </Box>
      </Box>

      {/* Mobile top bar */}
      <Flex
        display={{ base: "flex", md: "none" }}
        position="fixed" top="0" left="0" right="0"
        h="56px" bg={GREEN} align="center" px="16px" gap="14px"
        zIndex="98" boxShadow="0 2px 10px rgba(0,0,0,.18)"
      >
        <Box
          as="button" onClick={() => setSidebarOpen(!sidebarOpen)}
          bg="none" border="none" cursor="pointer" color="white" display="flex" p="0"
        >
          {sidebarOpen ? <FiX size={22} /> : <FiMenu size={22} />}
        </Box>
        <Text fontSize="15px" fontWeight="900" color={ORANGE}>
          {NAV.find(n => n.key === activeTab)?.label || "Dashboard"}
        </Text>
      </Flex>

      {/* Content */}
      <Box
        ml={{ base: "0", md: SIDEBAR_W }}
        flex="1"
        minH="100vh"
        pt={{ base: "56px", md: "0" }}
        p={{ base: "72px 16px 40px", md: "32px 36px 48px" }}
        overflowX="hidden"
      >
        {/* Section wrapper */}
        {activeTab !== "" ? (
          <Box
            bg="white" borderRadius="20px" border="1px solid rgba(0,74,51,.08)"
            boxShadow="0 2px 16px rgba(0,74,51,.06)" p={{ base: "20px", md: "28px 32px" }}
            minH="calc(100vh - 80px)"
          >
            {renderContent()}
          </Box>
        ) : (
          renderContent()
        )}
      </Box>
    </Box>
  );
}
